<template>
  <right-side-drawer :visible="notificationsVisible" @backdrop-click="toggleNotifications(false)">
    <div class="notification-list">
      <transition name="fade" mode="out-in">
        <base-inline-loader v-if="loading" key="loader" />
        <div v-else key="msg-list">
          <div class="header">
            <h2 class="title">{{ $t('notifications.listTitle') }}</h2>
            <transition name="fade" mode="out-in">
              <div v-if="messagesIds.length" class="actions-btn">
                <button class="cancel-btn" @click="deselectMessages">{{ $t('notifications.cancelBtn') }}</button>
                <button @click="deleteNotification(messagesIds)" class="delete-btn">
                  {{ $t('notifications.deleteBtn') }}
                </button>
              </div>
              <button v-else @click="toggleNotifications(false)">
                <img src="/assets/images/close_red.svg" alt="close-btn">
              </button>
            </transition>
          </div>
          <h3 v-if="!messages.length" class="placeholder">{{ $t('notifications.emptyList') }}</h3>
          <div v-else class="body" v-bar="{preventParentScroll: true, scrollThrottle: 15, scrollingPhantomDelay: 200}">
            <div>
              <div class="message" :class="{active: activeNotification === message.id}"
                   v-for="(message, index) in messages" :key="'message'+index">
                <base-checkbox v-model="selectedMessages[message.id]" variant="red" size="small"
                               class="message-checkbox" required></base-checkbox>
                <div class="message-wrapper" @click="getNotificationDetails(message)">
                  <h3 class="message-title" :class="{read: message.read}">{{ message.title }}</h3>
                  <div class="date">{{ message.creationtime }}</div>
                  <p class="message-description" v-html="message.description" />
                </div>
              </div>
            </div>
          </div>
          <div v-if="totalNotifications" class="pagination">
            <button @click="getNotificationsData('prev')" :disabled="!hasPrev">
              <i class="icon-sc-chevron-left"></i>
            </button>
            <span>{{ currentPage + 1 }} / {{ totalPages }}</span>
            <button @click="getNotificationsData('next')" :disabled="!hasNext">
              <i class="icon-sc-chevron-right"></i>
            </button>
          </div>
        </div>
      </transition>
    </div>
    <notification-details slot="details"
                          v-if="notificationsDetails"
                          :notification="notificationsDetails"
                          @close-details="closeDetails"
                          @delete-notification="deleteNotification" />
  </right-side-drawer>
</template>

<script>
import RightSideDrawer from '@/components/common/right-side-drawer'
import { customEvent } from '@/services/customEventBus'
import errorMessageHandler from '@/mixins/errorMessageHandler'
import NotificationDetails from '@/components/notifications/notification-details'

export default {
  name: 'notification-list',
  components: { RightSideDrawer, NotificationDetails },
  mixins: [errorMessageHandler],
  data () {
    return {
      notificationsVisible: false,
      notificationsDetails: null,
      selectedMessages: {},
      activeNotification: null,
      messages: [],
      loading: true,
      totalNotifications: null,
      currentPage: 0,
      totalPages: null,
      hasNext: null,
      hasPrev: null
    }
  },
  computed: {
    messagesIds () {
      return Object.keys(this.selectedMessages).filter(key => this.selectedMessages[key])
    }
  },
  methods: {
    deselectMessages () {
      for (const i in this.selectedMessages) this.selectedMessages[i] = false
    },
    closeNotifications () {
      this.notificationsVisible = false
    },
    toggleNotifications (state) {
      this.notificationsVisible = state
      this.closeDetails()
    },
    getNotificationsCount () {
      this.$apiRequest.get('user/notifications/count')
        .then(response => {
          this.totalNotifications = response.data.count || 0
          customEvent.$emit('notification:count', response.data.count)
        })
        .catch(e => {
          this.handleErrorMessage(e.response.data)
        })
    },
    getNotificationsData (direction) {
      if (direction === 'prev' && this.hasPrev) {
        this.currentPage = this.currentPage - 1
        this.selectedMessages = {}
      } else if (direction === 'next' && this.hasNext) {
        this.currentPage = this.currentPage + 1
        this.selectedMessages = {}
      }
      this.$apiRequest.get(`user/notifications?currentPage=${this.currentPage}`)
        .then(response => {
          this.messages = response.data.results
          this.totalPages = response.data.pagination.numberOfPages
          this.hasNext = response.data.pagination.hasNext
          this.hasPrev = response.data.pagination.hasPrevious
        })
        .catch(e => {
          this.handleErrorMessage(e.response.data)
        })
        .finally(() => {
          // right drawer animation is 200ms
          setTimeout(() => {
            this.loading = false
          }, 200)
        })
    },
    getNotificationDetails (item) {
      if (!this.notificationsDetails || this.notificationsDetails.id !== item.id) {
        this.activeNotification = item.id
        if (!item.read) {
          this.$apiRequest.get(`user/notifications/${item.id}`)
            .then(response => {
              this.notificationsDetails = response.data
              this.messages.forEach(obj => {
                if (obj.id === item.id) {
                  this.$set(obj, 'read', true)
                }
              })
            })
            .catch(e => {
              this.handleErrorMessage(e.response.data)
            })
        } else {
          this.notificationsDetails = item
        }
      } else {
        this.closeDetails()
      }
    },
    deleteNotification (message) {
      const recipientIds = {
        recipientIds: message
      }
      this.$apiRequest.delete('user/notifications', { data: { recipientIds } })
        .then(response => {
          if (response.status >= 200 && response.status < 400) {
            this.toast('success', {
              title: this.$t('toastMessages.successToastTitle'),
              message: this.$t('toastMessages.messagesDeleted')
            })
            if (message.length === this.messages.length && this.currentPage !== 0) {
              this.currentPage = this.currentPage - 1
            }
            this.getNotificationsCount()
            this.getNotificationsData()
            if (this.notificationsDetails && message.includes(this.notificationsDetails.id)) {
              this.notificationsDetails = null
              this.activeNotification = null
            }
            if (message.length > 1) {
              this.selectedMessages = {}
            } else {
              const { [message]: _, ...result } = this.selectedMessages
              this.selectedMessages = result
            }
          }
        })
        .catch(e => {
          this.handleErrorMessage(e.response.data)
        })
    },
    closeDetails () {
      this.notificationsDetails = null
      this.activeNotification = null
    }
  },
  watch: {
    notificationsVisible (newVal) {
      document.body.classList.add('vue-modal-open')
      document.getElementsByTagName('html')[0].classList.add('vue-modal-open')

      if (!newVal) {
        customEvent.$emit('notification:close')
        this.loading = true
        this.selectedMessages = {}
        this.getNotificationsCount()
        this.currentPage = 0
        document.body.classList.remove('vue-modal-open')
        document.getElementsByTagName('html')[0].classList.remove('vue-modal-open')
      }
    }
  },
  mounted () {
    this.getNotificationsCount()
    customEvent.$on('notification:toggle', (state) => this.toggleNotifications(state))
    customEvent.$on('notifications:list', () => this.getNotificationsData())
    customEvent.$on('notification-icon:loaded', () => {
      if (this.totalNotifications) {
        customEvent.$emit('notification:count', this.totalNotifications)
      }
    })
  },
  beforeDestroy () {
    customEvent.$off('notification:toggle')
    customEvent.$off('notifications:list')
  }
}
</script>
<style scoped lang="scss" src="./notification-comp-styles/notification-list.scss"></style>
